import React, { useState, useEffect, useCallback } from "react";
import ReactQuill from "react-quill";
import { FaEdit, FaTrash } from "react-icons/fa";
import { COURSES_URL } from "../../Services/courseService";
import './UserNotes.css';
import { toast } from 'react-toastify';

import 'react-quill/dist/quill.snow.css';

const MAX_CHAR_COUNT = 1400;

const NotesSection = ({ handlePlay, handlePause, videoRef, videoId, videoName, courseTitle, userId, courseId }) => {
    const [notes, setNotes] = useState([]);
    const [noteContent, setNoteContent] = useState("");
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [addingNoteAtvideoTime, setAddingNoteAtvideoTime] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteNoteId, setDeleteNoteId] = useState(null);
    const isVideoRefValid = videoRef?.current !== null;
    const [sortOrder, setSortOrder] = useState('recent');
    const [charCount, setCharCount] = useState(0);

    const fetchNotes = useCallback(async () => {
        try {
            const response = await fetch(`${COURSES_URL}/course/getNotes?userId=${userId}&courseId=${courseId}`);
            const data = await response.json();
            if (data.length === 0) {
                setNotes([]);
            } else {
                setNotes(data.reverse());
            }
        } catch (error) {
            console.error("Error fetching notes:", error);
        }
    }, [userId, courseId]);


    useEffect(() => {
        fetchNotes();
    }, [fetchNotes, courseId]);

    const handleAddNote = (videoTime = null) => {
        handlePause();
        const currentTime = videoTime !== null ? videoTime : (isVideoRefValid ? Math.floor(videoRef.current.currentTime) : 0);
        setAddingNoteAtvideoTime(currentTime);
        setEditingNoteId("new");
        setNoteContent("");
        setCharCount(0);
    };

    const formatTime = (time) => {
        if (isNaN(time) || time < 0) return "0:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    };

    const parsevideoTime = (videoTime) => {
        if (videoTime.includes(":")) {
            const [minutes, seconds] = videoTime.split(":");
            return parseInt(minutes) * 60 + parseInt(seconds);
        }
        return parseFloat(videoTime);
    };

    const handleSaveNote = async (noteId = null) => {
        toast.dismiss();
        if (!noteContent.trim()) {
            toast.error("Enter some notes to save");
            return;
        }

        let video_Time;
        let video_Id;
        let video_Name;

        if (noteId) {
            const existingNote = notes.find(note => note.id === noteId);
            video_Time = existingNote.videoTime;
            video_Id = existingNote.videoId;
            video_Name = existingNote.videoName;
        } else {
            video_Time = addingNoteAtvideoTime || (videoRef?.current ? Math.floor(videoRef.current.currentTime) : 0);
            video_Id = videoId;
            video_Name = videoName;
        }

        try {
            await fetch(`${COURSES_URL}/course/createOrUpdateNotes?userId=${userId}&courseId=${courseId}&noteContent=${encodeURIComponent(noteContent)}&videoTime=${video_Time}&videoId=${video_Id}&videoName=${video_Name}`, {
                method: "POST"
            });
            fetchNotes();
        } catch (error) {
            console.error("Error saving note:", error);
        }

        handlePlay();
        setEditingNoteId(null);
        setAddingNoteAtvideoTime(null);
    };

    const handleDeleteNote = (noteId) => {
        handlePause();
        setDeleteNoteId(noteId);
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = async () => {
        const noteId = deleteNoteId;
        const existingNote = notes.find(note => note.id === noteId);

        const videoTime = noteId && existingNote ? existingNote.videoTime : addingNoteAtvideoTime;

        const video_Id = existingNote.videoId;
        const video_Name = existingNote.videoName;

        const updatedNotes = notes.filter(note => note.id !== noteId);
        setNotes(updatedNotes);

        try {
            await fetch(`${COURSES_URL}/course/deleteNotes?userId=${userId}&courseId=${courseId}&videoTime=${videoTime}&videoId=${video_Id}&videoName=${video_Name}`, {
                method: "DELETE"
            });

            setTimeout(fetchNotes, 500);
        } catch (error) {
            console.error("Error deleting note:", error);
            setNotes(notes);
        }

        handlePlay();
        setShowDeleteConfirm(false);
    };


    const handleEditNote = (note) => {
        handlePause();
        setEditingNoteId(note.id);
        setNoteContent(note.noteContent);
        setCharCount(note.noteContent.length);
    };

    const handleSortChange = (e) => {
        const selectedSortOrder = e.target.value;
        setSortOrder(selectedSortOrder);

        let sortedNotes = [...notes];
        if (selectedSortOrder === 'oldest') {
            sortedNotes = sortedNotes.reverse();
        }

        setNotes(sortedNotes);
    };

    const handleNoteContentChange = (content) => {
        const contentLength = content.length;

        if (contentLength <= MAX_CHAR_COUNT) {
            setNoteContent(content);
            setCharCount(contentLength);
        } else {
            // Trim the content to 1400 characters if it exceeds
            setNoteContent(content.substring(0, MAX_CHAR_COUNT));
            setCharCount(MAX_CHAR_COUNT);
        }
    };

    const handleShowDeleteConfirmFalse = () => {
        setShowDeleteConfirm(false);
        handlePlay();
    };

    const handleEditingNoteIdNull = () => {
        setEditingNoteId(null);
        handlePlay();
    };

    const handleQuillKeyboardControls = (e) => {
        e.stopPropagation();
    };

    return (
        <div className="notes-section">
            <div className="add-notes-section">
                <button className="add-note-btn" onClick={() => handleAddNote()}>Create a new note</button>
                {editingNoteId === "new" && (
                    <div className="note-input-section">
                        <ReactQuill
                            value={noteContent}
                            onChange={handleNoteContentChange}
                            onKeyDown={handleQuillKeyboardControls}
                            modules={{
                                toolbar: [
                                    [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                    ['bold', 'italic', 'underline'],
                                    ['blockquote'],
                                    [{ 'align': [] }],
                                    ['link'],
                                    ['clean']
                                ],
                            }}
                            formats={['header', 'font', 'list', 'bold', 'italic', 'underline', 'blockquote', 'align', 'link']}
                        />
                        <button className="save-note-btn" onClick={() => handleSaveNote()}>Save</button>
                        <button className="cancel-note-btn" onClick={() => handleEditingNoteIdNull()}>Cancel</button>
                        <div className="char-count">
                            {charCount === 0 ? "Enter some content" : `${charCount}/${MAX_CHAR_COUNT} characters`}
                        </div>
                    </div>
                )}
            </div>

            {notes.length > 0 && (
                <div className="sort-options">
                    <select onChange={handleSortChange} value={sortOrder}>
                        <option value="recent">Sort by Recent</option>
                        <option value="oldest">Sort by Oldest</option>
                    </select>
                </div>
            )}

            <div className="notes-list">
                {notes.map((note) => (
                    <div className="note-item" key={note.id}>
                        <div className="note-header">
                            <span className="note-videoTime">{formatTime(parsevideoTime(note.videoTime))}</span>
                            <span className="note-video-name">{courseTitle}</span>
                            {editingNoteId !== note.id ? (
                                <div className="note-action-icons">
                                    <FaEdit className="edit-icon" id="icon" onClick={() => handleEditNote(note)} />
                                    <FaTrash className="delete-icon" id="icon" onClick={() => handleDeleteNote(note.id)} />
                                </div>
                            ) : (
                                <div className="note-action-icons">
                                    <button className="save-note-btn" onClick={() => handleSaveNote(note.id)}>Save</button>
                                    <button className="cancel-note-btn" onClick={() => handleEditingNoteIdNull()}>Cancel</button>
                                </div>
                            )}
                        </div>
                        <span className="note-video-name2">{note?.videoName}</span>
                        {editingNoteId === note.id ? (
                            <ReactQuill
                                value={noteContent}
                                onChange={handleNoteContentChange}
                                onKeyDown={handleQuillKeyboardControls}
                                modules={{
                                    toolbar: [
                                        [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
                                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                        ['bold', 'italic', 'underline'],
                                        ['blockquote'],
                                        [{ 'align': [] }],
                                        ['link'],
                                        ['clean']
                                    ],
                                }}
                                formats={['header', 'font', 'list', 'bold', 'italic', 'underline', 'blockquote', 'align', 'link']}
                            />
                        ) : (
                            <div className="note-content" dangerouslySetInnerHTML={{ __html: note.noteContent }}></div>
                        )}
                    </div>
                ))}
            </div>

            {showDeleteConfirm && (
                <div className="modal-overlay">
                    <div className="delete-overlay-modal">
                        <p>Are you sure you want to delete your note?</p>
                        <div className="modal-buttons">
                            <button className="confirm-btn" onClick={handleConfirmDelete}>Yes</button>
                            <button className="cancel-btn" onClick={handleShowDeleteConfirmFalse}>No</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotesSection;
